import { z } from "zod";
export const PROTECTED_CONTENT_CHUNK_BYTES = 96 * 1024;
export const MAX_PROTECTED_CONTENT_BYTES = 256 * 1024 * 1024;
const scope = {
  appId: z.string().regex(/^[a-z][a-z0-9-]{0,63}$/),
  subjectId: z.string().min(1).max(256),
  rootEntityId: z.string().min(1).max(128),
  keyId: z.string().uuid(),
};
export const ProtectedContentManifestSchema = z
  .object({
    ...scope,
    format: z.literal("toy-protected-content-v1"),
    generation: z.string().uuid(),
    wrappedKey: z.string().min(1).max(4096),
    chunkCount: z
      .number()
      .int()
      .min(1)
      .max(
        Math.ceil(MAX_PROTECTED_CONTENT_BYTES / PROTECTED_CONTENT_CHUNK_BYTES),
      ),
    plaintextBytes: z.number().int().min(1).max(MAX_PROTECTED_CONTENT_BYTES),
  })
  .strict()
  .refine(
    (v) =>
      v.chunkCount ===
      Math.ceil(v.plaintextBytes / PROTECTED_CONTENT_CHUNK_BYTES),
    "Invalid protected chunk count",
  );
export const ProtectedContentChunkSchema = z
  .object({
    ...scope,
    format: z.literal("toy-protected-chunk-v1"),
    generation: z.string().uuid(),
    index: z
      .number()
      .int()
      .min(0)
      .max(
        Math.ceil(MAX_PROTECTED_CONTENT_BYTES / PROTECTED_CONTENT_CHUNK_BYTES) -
          1,
      ),
    ciphertext: z
      .string()
      .regex(/^(?:[a-f0-9]{2})+$/)
      .min(58)
      .max((PROTECTED_CONTENT_CHUNK_BYTES + 28) * 2),
  })
  .strict();
export type ProtectedContentManifest = z.infer<
  typeof ProtectedContentManifestSchema
>;
export type ProtectedContentChunk = z.infer<typeof ProtectedContentChunkSchema>;
export function protectedManifestFromPayload(
  payload: unknown,
): ProtectedContentManifest | null {
  let value = payload;
  for (let depth = 0; depth < 2 && typeof value === "string"; depth++) {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== "object" || !("protected" in value))
    return null;
  return z
    .object({ protected: ProtectedContentManifestSchema })
    .strict()
    .parse(value).protected;
}
